import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ExternalLink, Globe, Loader2, Save } from "lucide-react";
import { api } from "@/lib/api";

const LANGS = ["en", "sv", "ar"];

const SECTION_KEYS = {
  hero: ["hero_badge", "hero_title", "hero_subtitle", "hero_cta_primary", "hero_cta_secondary"],
  features: ["features_title", "features_subtitle"],
  how: [
    "how_title",
    "how_step1_title", "how_step1_body",
    "how_step2_title", "how_step2_body",
    "how_step3_title", "how_step3_body",
  ],
  pricing: ["pricing_title", "pricing_subtitle"],
  contact: [
    "contact_title", "contact_sales", "contact_support",
    "contact_address", "contact_email",
    "contact_phone_eu", "contact_phone_gcc", "contact_phone_levant",
  ],
  footer: ["footer_tagline"],
};

function TextField({ id, label, value, onChange, rtl, multiline }) {
  const cls = "mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-600 focus:border-amber-500 focus:outline-none";
  return (
    <div>
      <label htmlFor={id} className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</label>
      {multiline ? (
        <textarea
          id={id}
          rows={3}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={id}
          dir={rtl ? "rtl" : "ltr"}
          className={cls}
        />
      ) : (
        <input
          id={id}
          type="text"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={id}
          dir={rtl ? "rtl" : "ltr"}
          className={cls}
        />
      )}
    </div>
  );
}

export default function AdminLanding() {
  const { t } = useTranslation();
  const [content, setContent] = useState({});
  const [dirty, setDirty] = useState({});
  const [busy, setBusy] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeLang, setActiveLang] = useState("en");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/platform/landing");
      setContent(data.items || {});
      setDirty({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load landing content");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function updateField(lang, key, value) {
    setContent((s) => ({
      ...s,
      [lang]: { ...(s[lang] || {}), [key]: value },
    }));
    setDirty((s) => ({ ...s, [lang]: true }));
  }

  async function saveLang(lang) {
    setBusy((s) => ({ ...s, [lang]: true }));
    try {
      const payload = Object.fromEntries(
        Object.entries(content[lang] || {}).filter(
          ([k]) => k !== "id" && k !== "language" && k !== "created_at" && k !== "updated_at"
        )
      );
      const { data } = await api.patch(`/platform/landing/${lang}`, payload);
      setContent((s) => ({ ...s, [lang]: data }));
      setDirty((s) => ({ ...s, [lang]: false }));
      toast.success(t("adminLanding.saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("adminLanding.saveFailed"));
    } finally {
      setBusy((s) => ({ ...s, [lang]: false }));
    }
  }

  const rtl = activeLang === "ar";
  const cur = useMemo(() => content[activeLang] || {}, [content, activeLang]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-400" data-testid="admin-landing-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> …
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-landing-page">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-500/15 text-amber-300 ring-1 ring-inset ring-amber-500/30">
            <Globe className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-white sm:text-4xl">
              {t("adminLanding.title")}
            </h1>
            <p className="mt-1 text-sm text-slate-400">{t("adminLanding.subtitle")}</p>
          </div>
        </div>
        <a
          href="/"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 bg-slate-900 px-3 py-1.5 text-sm text-slate-200 hover:border-amber-500/50 hover:text-amber-300"
          data-testid="admin-landing-preview"
        >
          <ExternalLink className="h-4 w-4" /> {t("adminLanding.preview")}
        </a>
      </header>

      {/* Language tabs */}
      <div className="flex flex-wrap gap-2" data-testid="admin-landing-lang-tabs">
        {LANGS.map((l) => (
          <button
            key={l}
            onClick={() => setActiveLang(l)}
            data-testid={`admin-landing-tab-${l}`}
            className={[
              "rounded-lg px-4 py-2 text-sm font-medium transition-colors",
              activeLang === l
                ? "bg-amber-500 text-slate-950"
                : "border border-slate-700 bg-slate-900 text-slate-200 hover:border-amber-500/50",
            ].join(" ")}
          >
            {t(`adminLanding.langTabs.${l}`)}
            {dirty[l] ? " •" : ""}
          </button>
        ))}
      </div>

      <div className="space-y-6" key={activeLang}>
        {Object.entries(SECTION_KEYS).map(([section, keys]) => (
          <section
            key={section}
            className="rounded-2xl border border-slate-800 bg-slate-900/70 p-5"
            data-testid={`admin-landing-section-${section}`}
          >
            <h2 className="mb-4 text-sm font-semibold text-slate-100">
              {t(`adminLanding.sections.${section}`)}
            </h2>
            <div className="grid gap-4 sm:grid-cols-2">
              {keys.map((k) => (
                <div
                  key={k}
                  className={
                    k === "hero_subtitle" || k === "features_subtitle" || k === "how_step1_body" || k === "how_step2_body" || k === "how_step3_body" || k === "contact_address"
                      ? "sm:col-span-2"
                      : ""
                  }
                >
                  <TextField
                    id={`cms-${activeLang}-${k}`}
                    label={t(`adminLanding.labels.${k}`)}
                    value={cur[k]}
                    onChange={(v) => updateField(activeLang, k, v)}
                    rtl={rtl}
                    multiline={k === "hero_subtitle" || k === "features_subtitle" || k.endsWith("_body") || k === "contact_address"}
                  />
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>

      <div className="sticky bottom-0 -mx-4 flex justify-end border-t border-slate-800 bg-[#0b1220]/95 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6 md:-mx-10 md:px-10">
        <button
          onClick={() => saveLang(activeLang)}
          disabled={busy[activeLang] || !dirty[activeLang]}
          data-testid={`admin-landing-save-${activeLang}`}
          className="inline-flex items-center gap-2 rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-60"
        >
          {busy[activeLang] ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {t("adminLanding.save", { lang: t(`adminLanding.langTabs.${activeLang}`) })}
        </button>
      </div>
    </div>
  );
}
